import {
  createPluginFactory,
  RangeBeforeOptions,
  ELEMENT_LINK
} from "@udecode/plate";
import { isUrl } from "./helpers";
import { withLink } from "./withLink";

export interface LinkPlugin {
  /**
   * Allow custom config for rangeBeforeOptions.
   * @example default
   * {
   *   matchString: ' ',
   *   skipInvalid: true,
   *   afterMatch: true,
   * }
   */
  rangeBeforeOptions?: RangeBeforeOptions;

  /**
   * Callback to validate an url.
   * @default isUrl
   */
  isUrl?: (text: string) => boolean;

  /**
   * Callback to optionally get the href for a url
   * @returns href: an optional link to be used that is different from the text content (example https://google.com for google.com)
   */
  getUrlHref?: (url: string) => string | undefined;

  /**
   * On keyboard shortcut or toolbar mousedown, get the link url by calling this promise. The
   * default behavior is to use the browser's native `prompt`.
   */
  getLinkUrl?: (prevUrl: string | null) => Promise<string | null>;
}

/**
 * Enables support for hyperlinks.
 */
export const createLinkPlugin = createPluginFactory<LinkPlugin>({
  key: ELEMENT_LINK,
  isElement: true,
  isInline: true,
  props: ({ element }) => ({ nodeProps: { href: element?.url } }),
  withOverrides: withLink,
  options: {
    isUrl: isUrl,
    rangeBeforeOptions: {
      matchString: " ",
      skipInvalid: true,
      afterMatch: true,
    }
  },
  then: (editor, { type }) => ({
    deserializeHtml: {
      rules: [
        {
          validNodeName: "A",
        },
      ],
      getNode: (el) => ({
        type,
        url: el.getAttribute("href"),
      }),
    },
  }),
});
